import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Calendar,
  Building2,
  User,
  MapPin,
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  Plus,
  MessageSquare,
  Send,
  ChevronLeft,
  ChevronRight,
  Bell,
  CalendarDays,
  LayoutGrid,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScheduleVisitDrawer } from "./ScheduleVisitDrawer";
import {
  REMINDER_24H_MARKER,
  REMINDER_2H_MARKER,
  useRealEstateContext,
  visitReminderKind,
} from "../providers/RealEstateContext";
import { openWhatsAppWeb } from "@/lib/whatsapp";
import { cn } from "@/lib/utils";
import type {
  SiteVisit,
  SiteVisitStatus,
  VisitOutcome,
} from "@/types/realEstate";
import { useIsMobile } from "@/hooks/use-mobile";
import MobileHeader from "../layout/MobileHeader";

type ViewMode = "list" | "day" | "week";

const OUTCOME_LABELS: Record<VisitOutcome, string> = {
  Completed: "Completed",
  No_Show: "No-show",
  Reschedule: "Reschedule",
  Cancelled: "Cancelled",
};

const pad2 = (n: number) => String(n).padStart(2, "0");

const toDateInputValue = (d: Date) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const toDatetimeLocalValue = (iso?: string) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const startOfWeekMonday = (d: Date) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
};

const isSameCalendarDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** Mid-morning default for calendar click-to-schedule (does not auto-select a contact). */
const toMidMorningDefault = (dateInputValue: string) =>
  `${dateInputValue}T11:00`;

const getStatusBadge = (status: SiteVisitStatus) => {
  switch (status) {
    case "Scheduled":
      return (
        <Badge className="bg-amber-500 text-white">
          <Clock className="w-3 h-3 mr-1" /> Scheduled
        </Badge>
      );
    case "Completed":
      return (
        <Badge className="bg-emerald-600 text-white">
          <CheckCircle2 className="w-3 h-3 mr-1" /> Completed
        </Badge>
      );
    case "Cancelled":
      return (
        <Badge className="bg-red-600 text-white">
          <XCircle className="w-3 h-3 mr-1" /> Cancelled
        </Badge>
      );
    case "No_Show":
      return (
        <Badge className="bg-slate-600 text-white">
          <AlertCircle className="w-3 h-3 mr-1" /> No Show
        </Badge>
      );
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

const isDueForReminder = (visit: SiteVisit, now = Date.now()) => {
  if (visit.status !== "Scheduled") return false;
  const notes = visit.feedback_notes || "";
  if (
    notes.includes(REMINDER_24H_MARKER) &&
    notes.includes(REMINDER_2H_MARKER)
  ) {
    return false;
  }
  return visitReminderKind(visit, now) != null;
};

export const SiteVisitList = () => {
  const isMobile = useIsMobile();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [scheduleInitialAt, setScheduleInitialAt] = useState<
    string | undefined
  >(undefined);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [selectedDate, setSelectedDate] = useState(() =>
    toDateInputValue(new Date()),
  );
  const [weekAnchor, setWeekAnchor] = useState(() => new Date());
  const [banner, setBanner] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [outcomeDraft, setOutcomeDraft] = useState<
    Record<string, { feedback: string; rescheduleAt: string; noNotes: boolean }>
  >({});
  const [busyVisitId, setBusyVisitId] = useState<string | null>(null);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [noShowWhatsAppPromptId, setNoShowWhatsAppPromptId] = useState<
    string | null
  >(null);

  const openScheduleDrawer = (initialAt?: string) => {
    setScheduleInitialAt(initialAt);
    setIsDrawerOpen(true);
  };

  const {
    siteVisits,
    properties,
    campaignAudience,
    refreshCampaignAudience,
    salesAgents,
    recordVisitOutcome,
    sendDueVisitReminders,
  } = useRealEstateContext();

  useEffect(() => {
    void refreshCampaignAudience();
  }, [refreshCampaignAudience]);

  const resolveLead = useCallback(
    (contactId?: string) =>
      campaignAudience.find((l) => String(l.id) === String(contactId)),
    [campaignAudience],
  );

  const resolveAgent = useCallback(
    (agentId?: string) =>
      salesAgents.find((a) => String(a.id) === String(agentId)),
    [salesAgents],
  );

  const resolveProperty = useCallback(
    (propertyId?: string) =>
      properties.find((p) => String(p.id) === String(propertyId)),
    [properties],
  );

  const dueReminderVisits = useMemo(
    () => siteVisits.filter((v) => isDueForReminder(v)),
    [siteVisits],
  );

  const handleOutcome = async (visitId: string, outcome: VisitOutcome) => {
    const draft = outcomeDraft[visitId] ?? {
      feedback: "",
      rescheduleAt: "",
      noNotes: false,
    };
    if (outcome === "Reschedule" && !draft.rescheduleAt) {
      setBanner({
        type: "error",
        text: "Pick a new date & time before rescheduling.",
      });
      return;
    }
    if (outcome === "Completed" && !draft.feedback.trim() && !draft.noNotes) {
      setBanner({
        type: "error",
        text: "Add feedback notes, or check “No notes” to confirm the visit had nothing to report.",
      });
      return;
    }

    setBusyVisitId(visitId);
    try {
      const result = await recordVisitOutcome(visitId, outcome, {
        feedback: draft.feedback || undefined,
        scheduled_at:
          outcome === "Reschedule"
            ? new Date(draft.rescheduleAt).toISOString()
            : undefined,
      });
      if (!result.ok) {
        setBanner({
          type: "error",
          text: result.error || "Could not save visit outcome.",
        });
        return;
      }
      setBanner({
        type: "success",
        text: `Visit marked as ${OUTCOME_LABELS[outcome]}.`,
      });
      setOutcomeDraft((prev) => {
        const next = { ...prev };
        delete next[visitId];
        return next;
      });
      if (outcome === "No_Show") {
        setNoShowWhatsAppPromptId(visitId);
      }
    } catch {
      setBanner({ type: "error", text: "Could not save visit outcome." });
    } finally {
      setBusyVisitId(null);
    }
  };

  const handleSendReminders = async () => {
    if (dueReminderVisits.length === 0) {
      setBanner({ type: "success", text: "No due reminders right now." });
      return;
    }
    setSendingReminders(true);
    try {
      const { sent } = await sendDueVisitReminders();
      setBanner({
        type: "success",
        text: `Sent ${sent} reminder${sent === 1 ? "" : "s"}.`,
      });
    } catch {
      setBanner({ type: "error", text: "Failed to send reminders." });
    } finally {
      setSendingReminders(false);
    }
  };

  const visitsForDay = useMemo(() => {
    const day = new Date(selectedDate + "T00:00:00");
    return siteVisits
      .filter((v) => isSameCalendarDay(new Date(v.scheduled_at), day))
      .sort(
        (a, b) =>
          new Date(a.scheduled_at).getTime() -
          new Date(b.scheduled_at).getTime(),
      );
  }, [siteVisits, selectedDate]);

  const weekDays = useMemo(() => {
    const start = startOfWeekMonday(weekAnchor);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [weekAnchor]);

  const visitsByWeekDay = useMemo(() => {
    const map = new Map<string, SiteVisit[]>();
    for (const day of weekDays) {
      map.set(toDateInputValue(day), []);
    }
    for (const visit of siteVisits) {
      const visitDay = new Date(visit.scheduled_at);
      for (const day of weekDays) {
        if (isSameCalendarDay(visitDay, day)) {
          map.get(toDateInputValue(day))!.push(visit);
          break;
        }
      }
    }
    for (const [, visits] of map) {
      visits.sort(
        (a, b) =>
          new Date(a.scheduled_at).getTime() -
          new Date(b.scheduled_at).getTime(),
      );
    }
    return map;
  }, [siteVisits, weekDays]);

  const jumpToDay = (d: Date) => {
    setSelectedDate(toDateInputValue(d));
    setViewMode("day");
  };

  const renderWhatsAppButton = (
    visit: SiteVisit,
    leadName: string,
    leadPhone: string,
    matchedProp: ReturnType<typeof resolveProperty>,
  ) => {
    const mapsUrl =
      matchedProp?.project?.google_maps_url || "https://maps.google.com";
    return (
      <Button
        size="sm"
        variant="outline"
        className="w-full border-emerald-600 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 text-xs"
        disabled={!leadPhone}
        onClick={() => {
          if (!leadPhone) return;
          const msg = `Namaste${leadName ? ` ${leadName}` : ""}! Reminder for your Site Visit at ${matchedProp?.project?.name || "our project"} scheduled for ${new Date(visit.scheduled_at).toLocaleString("en-IN")}. Maps: ${mapsUrl}`;
          openWhatsAppWeb(leadPhone, msg);
        }}
      >
        <Send className="w-3.5 h-3.5 mr-1.5" /> Launch WhatsApp Pass
      </Button>
    );
  };

  const renderOutcomeControls = (visit: SiteVisit) => {
    if (visit.status !== "Scheduled") return null;
    const draft = outcomeDraft[visit.id] ?? {
      feedback: "",
      rescheduleAt: toDatetimeLocalValue(visit.scheduled_at),
      noNotes: false,
    };
    const busy = busyVisitId === visit.id;
    const outcomes: VisitOutcome[] = [
      "Completed",
      "No_Show",
      "Reschedule",
      "Cancelled",
    ];
    const completedBlocked = !draft.feedback.trim() && !draft.noNotes;

    return (
      <div className="space-y-2 pt-2 border-t mt-2">
        <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
          Record outcome
        </p>
        <Textarea
          placeholder="Feedback / notes (optional)"
          className="min-h-14 text-xs"
          value={draft.feedback}
          disabled={busy}
          onChange={(e) =>
            setOutcomeDraft((prev) => ({
              ...prev,
              [visit.id]: { ...draft, feedback: e.target.value },
            }))
          }
        />
        <div className="flex items-center gap-2">
          <Checkbox
            id={`no-notes-${visit.id}`}
            checked={draft.noNotes}
            disabled={busy}
            onCheckedChange={(c) =>
              setOutcomeDraft((prev) => ({
                ...prev,
                [visit.id]: { ...draft, noNotes: c === true },
              }))
            }
          />
          <label
            htmlFor={`no-notes-${visit.id}`}
            className="text-[11px] text-muted-foreground"
          >
            No notes — nothing to report for this visit
          </label>
        </div>
        <Input
          type="datetime-local"
          className="text-xs h-8"
          value={draft.rescheduleAt}
          disabled={busy}
          onChange={(e) =>
            setOutcomeDraft((prev) => ({
              ...prev,
              [visit.id]: { ...draft, rescheduleAt: e.target.value },
            }))
          }
        />
        <div className="grid grid-cols-2 gap-1.5">
          {outcomes.map((outcome) => (
            <Button
              key={outcome}
              size="sm"
              variant={outcome === "Completed" ? "default" : "outline"}
              className={cn(
                "text-[11px] h-7",
                outcome === "Completed" &&
                  "bg-primary hover:bg-primary/90 text-primary-foreground",
                outcome === "Cancelled" &&
                  "border-red-300 text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30",
              )}
              disabled={busy || (outcome === "Completed" && completedBlocked)}
              title={
                outcome === "Completed" && completedBlocked
                  ? "Add feedback or check “No notes” first"
                  : undefined
              }
              onClick={() => handleOutcome(visit.id, outcome)}
            >
              {OUTCOME_LABELS[outcome]}
            </Button>
          ))}
        </div>
      </div>
    );
  };

  const renderVisitCard = (
    visit: SiteVisit,
    variant: "full" | "compact" | "dense" = "full",
  ) => {
    const matchedProp = resolveProperty(visit.property_id);
    const lead = resolveLead(visit.contact_id);
    const agent = resolveAgent(visit.agent_id);
    const leadName = lead?.name || `Contact #${visit.contact_id}`;
    const leadPhone = lead?.phone || "";
    const unitLabel = matchedProp?.unit_number
      ? `${matchedProp.project?.name || "Project"} - Unit ${matchedProp.unit_number}`
      : "Site Visit";

    if (variant === "compact") {
      return (
        <div
          key={visit.id}
          className="flex items-start justify-between gap-3 p-3 rounded-lg border bg-card hover:shadow-sm transition-shadow"
        >
          <div className="min-w-0 space-y-0.5">
            <p className="text-sm font-semibold truncate flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-primary shrink-0" />
              {new Date(visit.scheduled_at).toLocaleTimeString("en-IN", {
                timeStyle: "short",
              })}
              <span className="text-muted-foreground font-normal">·</span>
              {leadName}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {unitLabel}
              {agent ? ` · ${agent.name}` : ""}
            </p>
          </div>
          {getStatusBadge(visit.status)}
        </div>
      );
    }

    const dense = variant === "dense";

    return (
      <Card
        key={visit.id}
        className={cn(
          "hover:shadow-md transition-shadow border-muted flex flex-col justify-between",
          dense && "shadow-none",
        )}
      >
        <div>
          <CardHeader
            className={cn(
              "flex flex-row items-start justify-between space-y-0",
              dense ? "p-3 pb-2" : "pb-3",
            )}
          >
            <div className="min-w-0">
              <CardTitle
                className={cn(
                  "font-bold flex items-center gap-2",
                  dense ? "text-sm" : "text-base",
                )}
              >
                {dense ? (
                  <Clock className="w-3.5 h-3.5 text-primary shrink-0" />
                ) : (
                  <User className="w-4 h-4 text-primary shrink-0" />
                )}
                <span className="truncate">
                  {dense
                    ? `${new Date(visit.scheduled_at).toLocaleTimeString(
                        "en-IN",
                        {
                          timeStyle: "short",
                        },
                      )} · ${leadName}`
                    : leadName}
                </span>
              </CardTitle>
              <p
                className={cn(
                  "mt-1 flex items-center font-semibold text-foreground truncate",
                  dense ? "text-[11px]" : "text-xs text-muted-foreground",
                )}
              >
                <Building2 className="w-3.5 h-3.5 mr-1 text-primary shrink-0" />
                <span className="truncate">{unitLabel}</span>
              </p>
              {agent ? (
                <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
                  Agent: {agent.name}
                </p>
              ) : null}
            </div>
            {getStatusBadge(visit.status)}
          </CardHeader>

          <CardContent
            className={cn(
              "text-sm",
              dense ? "space-y-2 p-3 pt-0" : "space-y-3 pb-3",
            )}
          >
            {!dense && (
              <div className="bg-muted/50 p-3 rounded-lg space-y-1.5">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted-foreground">Scheduled Date:</span>
                  <span className="font-semibold text-foreground">
                    {new Date(visit.scheduled_at).toLocaleString("en-IN", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted-foreground">Location:</span>
                  <span className="font-medium text-foreground flex items-center">
                    <MapPin className="w-3 h-3 mr-1 text-muted-foreground" />
                    {matchedProp?.project?.location_address || "Project site"}
                  </span>
                </div>
              </div>
            )}

            {dense && (
              <p className="text-[11px] text-muted-foreground flex items-center gap-1 truncate">
                <MapPin className="w-3 h-3 shrink-0" />
                {matchedProp?.project?.location_address || "Project site"}
              </p>
            )}

            {visit.feedback_notes && (
              <div
                className={cn(
                  "text-xs bg-card rounded-lg border",
                  dense ? "p-2" : "p-2.5",
                )}
              >
                <span className="text-muted-foreground font-semibold flex items-center gap-1 mb-1">
                  <MessageSquare className="w-3 h-3 text-primary" /> Notes &
                  Feedback:
                </span>
                <p className="text-foreground italic line-clamp-2">
                  {visit.feedback_notes}
                </p>
              </div>
            )}

            {renderOutcomeControls(visit)}

            {visit.status === "No_Show" &&
              noShowWhatsAppPromptId === visit.id && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3 space-y-2">
                  <p className="text-xs font-medium text-amber-900 dark:text-amber-100">
                    Send a missed-visit WhatsApp to try to reschedule?
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="h-7 text-[11px] bg-amber-600 hover:bg-amber-700 text-white"
                      disabled={!leadPhone}
                      onClick={() => {
                        if (leadPhone) {
                          const msg = `Namaste${leadName ? ` ${leadName}` : ""}! We missed you at ${matchedProp?.project?.name || "the site visit"} today. Would you like to reschedule your visit?`;
                          openWhatsAppWeb(leadPhone, msg);
                        }
                        setNoShowWhatsAppPromptId(null);
                      }}
                    >
                      <Send className="w-3 h-3 mr-1" /> Send WhatsApp
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-[11px]"
                      onClick={() => setNoShowWhatsAppPromptId(null)}
                    >
                      Not now
                    </Button>
                  </div>
                </div>
              )}
          </CardContent>
        </div>

        <div className={cn("border-t", dense ? "p-2" : "p-3")}>
          {renderWhatsAppButton(visit, leadName, leadPhone, matchedProp)}
        </div>
      </Card>
    );
  };

  const viewToggle = (
    <div className="inline-flex rounded-lg border bg-muted/40 p-0.5">
      {(
        [
          { mode: "list" as const, label: "List", icon: LayoutGrid },
          { mode: "day" as const, label: "Day", icon: CalendarDays },
          { mode: "week" as const, label: "Week", icon: Calendar },
        ] as const
      ).map(({ mode, label, icon: Icon }) => (
        <Button
          key={mode}
          size="sm"
          variant={viewMode === mode ? "default" : "ghost"}
          className={cn(
            "h-8 px-3 text-xs gap-1.5",
            viewMode === mode &&
              "bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm",
          )}
          onClick={() => setViewMode(mode)}
        >
          <Icon className="w-3.5 h-3.5" />
          {label}
        </Button>
      ))}
    </div>
  );

  return (
    <>
      {isMobile ? (
        <MobileHeader>
          <h1 className="text-xl font-semibold truncate">Site Visits</h1>
        </MobileHeader>
      ) : null}
      <div
        className={cn(
          "p-6 space-y-6 max-w-[1600px] mx-auto",
          isMobile && "pt-18",
        )}
      >
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b pb-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2">
              <Calendar className="w-7 h-7 text-primary" />
              Site Visits & Field Dispatches
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Track site visit appointments, field sales executive assignments,
              and client feedback notes.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {viewToggle}
            <Button
              onClick={() => openScheduleDrawer(undefined)}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              <Plus className="w-4 h-4 mr-2" /> Schedule New Visit
            </Button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-lg border bg-muted/30 px-4 py-3">
          <div className="flex items-center gap-2 text-sm">
            <Bell className="w-4 h-4 text-amber-600" />
            <span className="font-medium text-foreground">
              Due reminders:{" "}
              <span className="text-primary">{dueReminderVisits.length}</span>
            </span>
            <span className="text-xs text-muted-foreground hidden sm:inline">
              (T-24h / T-2h windows)
            </span>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Button
              size="sm"
              variant="outline"
              className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
              disabled={sendingReminders || dueReminderVisits.length === 0}
              onClick={handleSendReminders}
            >
              <Send className="w-3.5 h-3.5 mr-1.5" />
              {sendingReminders ? "Sending…" : "Send due reminders"}
            </Button>
            <span className="text-xs text-muted-foreground text-right">
              T-24h / T-2h reminders also run on the automation schedule.
            </span>
          </div>
        </div>

        {banner && (
          <div
            className={cn(
              "rounded-lg border px-4 py-2.5 text-sm flex items-center justify-between gap-3",
              banner.type === "success"
                ? "bg-emerald-50 border-emerald-200 text-emerald-900 dark:bg-emerald-950/30 dark:border-emerald-800 dark:text-emerald-100"
                : "bg-red-50 border-red-200 text-red-900 dark:bg-red-950/30 dark:border-red-800 dark:text-red-100",
            )}
          >
            <span>{banner.text}</span>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 shrink-0"
              onClick={() => setBanner(null)}
            >
              Dismiss
            </Button>
          </div>
        )}

        {viewMode === "day" && (
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm font-medium text-foreground flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-primary" />
              Date
              <Input
                type="date"
                className="w-auto h-9"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </label>
            <Badge variant="outline" className="text-xs">
              {visitsForDay.length} visit{visitsForDay.length === 1 ? "" : "s"}
            </Badge>
          </div>
        )}

        {viewMode === "week" && (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-1">
              <Button
                size="icon"
                variant="outline"
                className="h-8 w-8"
                onClick={() => {
                  const d = new Date(weekAnchor);
                  d.setDate(d.getDate() - 7);
                  setWeekAnchor(d);
                }}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm font-semibold px-2 min-w-[180px] text-center">
                {weekDays[0].toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                })}{" "}
                –{" "}
                {weekDays[6].toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </span>
              <Button
                size="icon"
                variant="outline"
                className="h-8 w-8"
                onClick={() => {
                  const d = new Date(weekAnchor);
                  d.setDate(d.getDate() + 7);
                  setWeekAnchor(d);
                }}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="text-xs"
              onClick={() => setWeekAnchor(new Date())}
            >
              This week
            </Button>
          </div>
        )}

        {siteVisits.length === 0 ? (
          <p className="text-sm text-muted-foreground py-12 text-center">
            No site visits yet. Schedule one to get started.
          </p>
        ) : viewMode === "list" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...siteVisits]
              .sort(
                (a, b) =>
                  new Date(b.scheduled_at).getTime() -
                  new Date(a.scheduled_at).getTime(),
              )
              .map((visit) => renderVisitCard(visit))}
          </div>
        ) : viewMode === "day" ? (
          visitsForDay.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <p className="text-sm text-muted-foreground">
                No visits on{" "}
                {new Date(selectedDate + "T12:00:00").toLocaleDateString(
                  "en-IN",
                  {
                    dateStyle: "full",
                  },
                )}
                .
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  openScheduleDrawer(toMidMorningDefault(selectedDate))
                }
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Schedule a visit for
                this day
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {visitsForDay.map((visit) => renderVisitCard(visit, "dense"))}
            </div>
          )
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3">
            {weekDays.map((day) => {
              const key = toDateInputValue(day);
              const dayVisits = visitsByWeekDay.get(key) ?? [];
              const isToday = isSameCalendarDay(day, new Date());
              const dayLabel = day.toLocaleDateString("en-IN", {
                weekday: "short",
              });

              const isEmpty = dayVisits.length === 0;

              return (
                <button
                  key={key}
                  type="button"
                  onClick={() =>
                    isEmpty
                      ? openScheduleDrawer(toMidMorningDefault(key))
                      : jumpToDay(day)
                  }
                  title={
                    isEmpty
                      ? "Schedule a visit for this day"
                      : "View this day's visits"
                  }
                  className={cn(
                    "text-left rounded-lg border p-3 min-h-[120px] transition-colors hover:border-primary/50 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                    isToday && "border-primary bg-primary/5",
                  )}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className={cn(
                        "text-xs font-bold uppercase",
                        isToday ? "text-primary" : "text-muted-foreground",
                      )}
                    >
                      {dayLabel}
                    </span>
                    <Badge
                      variant={dayVisits.length > 0 ? "default" : "outline"}
                      className={cn(
                        "text-[10px] h-5 px-1.5",
                        dayVisits.length > 0 &&
                          "bg-primary text-primary-foreground",
                      )}
                    >
                      {dayVisits.length}
                    </Badge>
                  </div>
                  <p className="text-lg font-semibold text-foreground mb-2">
                    {day.getDate()}
                  </p>
                  {isEmpty ? (
                    <p className="flex items-center gap-1 text-[10px] text-muted-foreground">
                      <Plus className="w-3 h-3" /> Schedule
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {dayVisits.slice(0, 4).map((visit) => {
                        const lead = resolveLead(visit.contact_id);
                        const name =
                          lead?.name ||
                          `#${String(visit.contact_id).slice(0, 6)}`;
                        return (
                          <span
                            key={visit.id}
                            className="inline-block max-w-full truncate text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-foreground"
                          >
                            {name}
                          </span>
                        );
                      })}
                      {dayVisits.length > 4 && (
                        <span className="text-[10px] text-muted-foreground px-1">
                          +{dayVisits.length - 4} more
                        </span>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}

        <ScheduleVisitDrawer
          isOpen={isDrawerOpen}
          onOpenChange={setIsDrawerOpen}
          initialScheduledAt={scheduleInitialAt}
        />
      </div>
    </>
  );
};

export default SiteVisitList;
